import { h } from '@stencil/core';
import { SprykerOfferContent, ApigeeRecommendations, QdsImportance, DEBOUNCED_HANDLER_DELAY, ETabType, scrollToComponent, pesCatalogPageTypes, store, ACTION_TYPES, isAnalyticsEnabled, send, viewItemListEvent, selectItemCTAEvent, buildRecommendationPayload, events, parseCommonURLToRouterParts, } from '@pes-ui/utils';
import { debounce } from 'lodash';
import { isBrowser, isMobile } from '../../globals/utils';
import { deleteStickyBarTab, observeIntersectionWithHeader, setStickyBarTab, setTabActive } from '../../globals/stickyBar.utils';
import { getAnalyticsPageLoadData } from '../../globals/utils/helpers';
import { buildPBSURL, isAppRoutingEnabled, navigateWithRouter } from '../pes-router/utils';
import PesRoute from '../pes-router/pes-route';
const TAB_PREFIX = '#tab-';
const ONETRUST_ACTIVEGROUP = 'C0003';
const ENABLED_GROUP_CODE = ':1';
const DISABLED_GROUP_CODE = ':0';
const ONE_TRUST_COOKIE_REGEX = new RegExp('OptanonConsent=');
const TEALIUM_COOKIE_REGEX = new RegExp('TEAL=');
const VISITOR_ID_REGEX = new RegExp('TEAL=v:[^\\$]+\\$', 'g');
const TEALIUM_COOKIE_START = 'TEAL=v:';
export class PesRecommendations {
  constructor() {
    this.observer = null;
    this.hasContentForAnyTab = false;
    this.windowResizeHandler = () => {
      this.isMobile = isMobile();
    };
    this.debouncedHandleResize = debounce(this.windowResizeHandler, DEBOUNCED_HANDLER_DELAY);
    this.pageType = undefined;
    this.pageId = undefined;
    this.countryCode = undefined;
    this.languageCode = undefined;
    this.isLoggedIn = false;
    this.selectedIndex = 0;
    this.title = undefined;
    this.methodId = undefined;
    this.recommendations = undefined;
    this.fetchedData = [];
    this.isMobile = isMobile();
    this.analyticsPageData = undefined;
  }
  connectedCallback() {
    window.addEventListener('resize', this.debouncedHandleResize);
  }
  disconnectedCallback() {
    var _a;
    (_a = this.unsubscribe) === null || _a === void 0 ? void 0 : _a.call(this);
    window.removeEventListener('resize', this.debouncedHandleResize);
    this.cancelObserver();
  }
  handleTabClicked(event) {
    if (!this.el || event.detail.type !== ETabType.RECOMMENDATIONS)
      return;
    scrollToComponent(this.el);
  }
  tabShowHandler(event) {
    var _a;
    const target = event.target;
    const { id } = target;
    const newIndex = Number(id.replace(TAB_PREFIX, ''));
    if (newIndex === this.selectedIndex)
      return;
    this.selectedIndex = newIndex;
    this.sendGAEvent(viewItemListEvent, events.VIEW_ITEM_LIST, (_a = this.fetchedData[this.selectedIndex]) === null || _a === void 0 ? void 0 : _a.data);
    this.setTabRecommendations();
  }
  getOfferEntities(list) {
    return list === null || list === void 0 ? void 0 : list.map(({ recordID, type }) => ({
      id: recordID,
      type,
    }));
  }
  cancelObserver() {
    var _a, _b;
    (_b = (_a = this.observer) === null || _a === void 0 ? void 0 : _a.disconnect) === null || _b === void 0 ? void 0 : _b.call(_a);
    this.observer = null;
  }
  observe() {
    if (!this.el)
      return;
    this.cancelObserver();
    this.observer = observeIntersectionWithHeader(this.el, (entries) => {
      var _a;
      if (!((_a = entries[0]) === null || _a === void 0 ? void 0 : _a.isIntersecting))
        return null;
      setTabActive({ type: ETabType.RECOMMENDATIONS, title: this.title });
      this.setTabActive.emit();
    });
  }
  initializeStickyBarTab() {
    this.observe();
  }
  async getCarouselData(list) {
    try {
      const response = await SprykerOfferContent.fetchOfferCatalog({
        brand: '',
        languageCode: this.languageCode,
        countryCode: this.countryCode,
        rendition: 'rendition_369_jpg',
        payload: { entities: this.getOfferEntities(list) },
      });
      if (response.error)
        throw response.error;
      const result = response.result;
      if (!this.isLoggedIn && !isAppRoutingEnabled())
        return result;
      if (!isBrowser())
        return result;
      for (const item of result.data) {
        if (!item)
          continue;
        item.route = new PesRoute({ page: item.type, pageId: item.id });
        item.url = buildPBSURL(parseCommonURLToRouterParts(item.url));
      }
      return result;
    }
    catch (error) {
      console.error(`[PES] Could not fetch data for recommended items:\n${error === null || error === void 0 ? void 0 : error.message}`);
      return null;
    }
  }
  async getRecommendations() {
    const { pageType, pageId } = this;
    try {
      const { result, error } = await ApigeeRecommendations.fetchRecommendations({
        apiKey: '',
        queryParams: {
          userId: this.getVisitorId(),
          countryCode: this.countryCode,
          pageType,
          pageId,
        },
      });
      if (error)
        throw error;
      return result;
    }
    catch (error) {
      console.error(`[PES] Could not fetch recommendations:\n${error === null || error === void 0 ? void 0 : error.message}`);
      return null;
    }
  }
  getVisitorId() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    let customerConsent = false;
    let visitorId = '';
    const allCookies = document.cookie || '';
    for (const cookie of allCookies.split(';')) {
      if (ONE_TRUST_COOKIE_REGEX.test(cookie)) {
        const encodedGroupEnabled = encodeURIComponent(ONETRUST_ACTIVEGROUP + ENABLED_GROUP_CODE);
        const encodedGroupDisabled = encodeURIComponent(ONETRUST_ACTIVEGROUP + DISABLED_GROUP_CODE);
        if (cookie.includes(encodedGroupDisabled))
          return '';
        if (cookie.includes(encodedGroupEnabled))
          customerConsent = true;
        continue;
      }
      if (TEALIUM_COOKIE_REGEX.test(cookie)) {
        const visitorIdMatch = cookie.match(VISITOR_ID_REGEX) || [];
        visitorId = (_d = (_c = (_b = (_a = visitorIdMatch[0]) === null || _a === void 0 ? void 0 : _a.replace) === null || _b === void 0 ? void 0 : _b.call(_a, TEALIUM_COOKIE_START, '')) === null || _c === void 0 ? void 0 : _c.slice) === null || _d === void 0 ? void 0 : _d.call(_c, 0, -1);
      }
    }
    if (customerConsent && visitorId)
      return visitorId;
    if (customerConsent)
      return ((_f = (_e = window === null || window === void 0 ? void 0 : window.tealium) === null || _e === void 0 ? void 0 : _e.getVisitorId) === null || _f === void 0 ? void 0 : _f.call(_e)) || '';
    if (visitorId)
      return (((_g = window === null || window === void 0 ? void 0 : window.OnetrustActiveGroups) === null || _g === void 0 ? void 0 : _g.includes(ONETRUST_ACTIVEGROUP)) && visitorId) || '';
    return (((_h = window === null || window === void 0 ? void 0 : window.OnetrustActiveGroups) === null || _h === void 0 ? void 0 : _h.includes(ONETRUST_ACTIVEGROUP)) && ((_k = (_j = window === null || window === void 0 ? void 0 : window.tealium) === null || _j === void 0 ? void 0 : _j.getVisitorId) === null || _k === void 0 ? void 0 : _k.call(_j))) || '';
  }
  sendGAEvent(event, gaEvent, offerItem) {
    var _a, _b;
    if (!isAnalyticsEnabled())
      return;
    const analyticsPageData = getAnalyticsPageLoadData();
    const selectedRecommendation = (_b = (_a = this.recommendations) === null || _a === void 0 ? void 0 : _a.tabs) === null || _b === void 0 ? void 0 : _b[this.selectedIndex];
    if (!selectedRecommendation || !offerItem)
      return;
    const params = {
      offerItem,
      gaEvent,
      selectedRecommendation,
      title: this.title,
      methodId: this.methodId,
      analyticsPageTopCategory: (analyticsPageData === null || analyticsPageData === void 0 ? void 0 : analyticsPageData.pageTopCategory) || '',
    };
    const ecommerceEventObject = buildRecommendationPayload(params);
    send(event, ecommerceEventObject);
  }
  async setTabRecommendations() {
    var _a, _b, _c, _d;
    if ((_b = (_a = this.fetchedData[this.selectedIndex]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.length)
      return;
    const carouselData = await this.getCarouselData(this.recommendations.tabs[this.selectedIndex].records);
    if (!this.hasContentForAnyTab && ((_c = carouselData === null || carouselData === void 0 ? void 0 : carouselData.data) === null || _c === void 0 ? void 0 : _c.length)) {
      this.hasContentForAnyTab = true;
      if (this.pageType === pesCatalogPageTypes.PRODUCT) {
        this.initializeStickyBarTab();
        setStickyBarTab(ETabType.RECOMMENDATIONS, [this.title]);
      }
    }
    if (!carouselData) {
      this.fetchedData[this.selectedIndex] = null;
    }
    else {
      this.fetchedData[this.selectedIndex] = Object.assign(Object.assign({}, carouselData), { data: carouselData.data.filter((item) => item !== null) });
    }
    this.fetchedData = [...this.fetchedData];
    this.sendGAEvent(viewItemListEvent, events.VIEW_ITEM_LIST, (_d = this.fetchedData[this.selectedIndex]) === null || _d === void 0 ? void 0 : _d.data);
  }
  async loadData() {
    var _a;
    try {
      this.hasContentForAnyTab = false;
      deleteStickyBarTab(ETabType.RECOMMENDATIONS);
      const { title, method_id: methodId, recommendations } = (await this.getRecommendations()) || {};
      this.title = title;
      this.methodId = methodId;
      this.recommendations = recommendations;
      if (!((_a = recommendations === null || recommendations === void 0 ? void 0 : recommendations.tabs) === null || _a === void 0 ? void 0 : _a.length)) {
        console.warn('[PES]: No recommendations received from API.');
        return;
      }
      this.setTabRecommendations();
    }
    catch (error) {
      console.error(`[PES]: Error fetching products card data:\n${error.message}`);
    }
  }
  componentWillLoad() {
    if (!isBrowser())
      return;
    this.loadData();
    this.unsubscribe = store.subscribe(() => {
      const { lastActionType } = store.getState().product;
      if (lastActionType !== ACTION_TYPES.SET_ACTIVE_PRODUCT_TAB)
        return;
      this.loadData();
    });
  }
  render() {
    var _a, _b, _c, _d, _e, _f;
    if (!isBrowser() || !this.hasContentForAnyTab)
      return null;
    const hasTabs = Boolean((_b = (_a = this.recommendations) === null || _a === void 0 ? void 0 : _a.tabs) === null || _b === void 0 ? void 0 : _b.length);
    const hasRecommendations = Boolean((_e = (_d = (_c = this.fetchedData) === null || _c === void 0 ? void 0 : _c[this.selectedIndex]) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.length);
    const selectedFetchedData = this.fetchedData[this.selectedIndex];
    return (h("div", null, hasTabs && (h("div", { class: "pes-recommendations" }, h("pes-section-header", { slot: "header", class: "pes-recommendations__title" }, this.title), h("div", null, h("div", { class: "range-tab" }, this.recommendations.tabs.map((tab, index) => h("qds-tab", { key: tab.name || `${TAB_PREFIX}${index}`, id: `${TAB_PREFIX}${index}`, text: tab.name, size: "large", selected: index === this.selectedIndex, ref: (el) => {
        if (!el)
          return;
        el.addEventListener('click', (event) => this.tabShowHandler(event));
      } }))), h("qds-divider", { importance: QdsImportance.SUBDUED })), hasRecommendations && (h("qds-web-carousel", { carouselId: "top-products", indicatorPosition: "top-end", "item-min-width": "200" }, h("div", { class: "pes-recommendations__carousel" }, (_f = selectedFetchedData === null || selectedFetchedData === void 0 ? void 0 : selectedFetchedData.data) === null || _f === void 0 ? void 0 : _f.map(({ image, url, name, description, id, type, route, }, index) => {
      var _a;
      return h("pes-item-card", { key: id, commercialReference: id, image: image, title: name, description: description, linkUrl: url, linkText: (_a = selectedFetchedData === null || selectedFetchedData === void 0 ? void 0 : selectedFetchedData.labels) === null || _a === void 0 ? void 0 : _a.viewDetailsLabel, onClick: (event) => {
          const item = [{
              index,
              name,
              type,
              id,
            }];
          this.sendGAEvent(selectItemCTAEvent, events.SELECT_ITEM_CTA, item);
          if (!this.isLoggedIn && !isAppRoutingEnabled())
            return;
          event.preventDefault();
          navigateWithRouter(route, event.currentTarget);
        } });
    }))))))));
  }
  static get is() { return "pes-recommendations"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["pes-recommendations.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["pes-recommendations.css"]
    };
  }
  static get properties() {
    return {
      "pageType": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "TPesCatalogPageType",
          "resolved": "\"\" | \"all-products\" | \"category\" | \"comparator\" | \"product\" | \"product-cart\" | \"range\" | \"subcategory\"",
          "references": {
            "TPesCatalogPageType": {
              "location": "import",
              "path": "@pes-ui/utils"
            }
          }
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "page-type",
        "reflect": false
      },
      "pageId": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "page-id",
        "reflect": false
      },
      "countryCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "country-code",
        "reflect": false
      },
      "languageCode": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "language-code",
        "reflect": false
      },
      "isLoggedIn": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-logged-in",
        "reflect": false,
        "defaultValue": "false"
      }
    };
  }
  static get states() {
    return {
      "selectedIndex": {},
      "title": {},
      "methodId": {},
      "recommendations": {},
      "fetchedData": {},
      "isMobile": {},
      "analyticsPageData": {}
    };
  }
  static get events() {
    return [{
        "method": "setTabActive",
        "name": "setTabActive",
        "bubbles": true,
        "cancelable": true,
        "composed": true,
        "docs": {
          "tags": [],
          "text": ""
        },
        "complexType": {
          "original": "any",
          "resolved": "any",
          "references": {}
        }
      }];
  }
  static get elementRef() { return "el"; }
  static get listeners() {
    return [{
        "name": "tabClicked",
        "method": "handleTabClicked",
        "target": "window",
        "capture": false,
        "passive": false
      }];
  }
}
